import {countdownNow, isShowPaused, onShowPause} from "./showPause";
import React, {useEffect, useRef, useState} from 'react';

const getSecondsUntil = (targetDate) =>
    Math.max(
        0,
        Math.floor((new Date(targetDate) - countdownNow()) / 1000)
    )

const Countdown = ({ targetDate }) => {
    const [secondsLeft, setSecondsLeft] = useState(getSecondsUntil(targetDate));

    useEffect(() => {
        const timer = setInterval(() => {
            setSecondsLeft(getSecondsUntil(targetDate));
        }, 500);

        return () => clearInterval(timer);
    }, [targetDate]);

    return <span>{secondsLeft}</span>;
};

export default Countdown;

// Build a CSS clip-path polygon for a regular N-sided shape inscribed in
// a 100%×100% box. First vertex points up (rotation = -90°).
const polygonClipPath = (sides) => {
    const pts = [];
    for (let i = 0; i < sides; i++) {
        const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
        const x = 50 + 50 * Math.cos(angle);
        const y = 50 + 50 * Math.sin(angle);
        pts.push(`${x.toFixed(3)}% ${y.toFixed(3)}%`);
    }
    return `polygon(${pts.join(', ')})`;
};

// Countdown badge for in-scene placement. Counts down to the absolute
// timestamp `roundEnd` (the value broadcast by the control machine based
// on Max's `sceneEndsAt`). `clockDrift` keeps every device aligned. Color
// shifts white → yellow (≤30s) → red (≤10s). Floors at 0.
//
// IMPORTANT: latches the first valid `roundEnd` it sees and ignores
// subsequent updates. The next scene swap remounts this component (its
// parent scene component changes) and the new instance latches the new
// value. This prevents the visible jump from old-clip-0s up to
// new-clip-Ns when the WebSocket happens to deliver the new `roundEnd`
// before the JFD scene transition lands on the player.
//
// Pass `sides` (e.g. 3, 5, 6, 8) to render as a regular polygon instead of
// a circle. Glow uses filter:drop-shadow so it traces the polygon shape.
export const CountdownBadge = ({roundEnd, clockDrift = 0, sides, style}) => {
    const [, force] = useState(0);
    useEffect(() => {
        const id = setInterval(() => force((n) => n + 1), 100);
        return () => clearInterval(id);
    }, []);

    const [latchedRoundEnd, setLatchedRoundEnd] = useState(null);
    const [latchedStart, setLatchedStart] = useState(null);
    // A resumed show sends the countdown again (shifted by the pause): take that one change, keeping the ring's start.
    const acceptShift = useRef(false);
    useEffect(() => onShowPause(() => { if (!isShowPaused()) acceptShift.current = true; }), []);
    useEffect(() => {
        if (latchedRoundEnd !== null) {
            if (acceptShift.current && typeof roundEnd === 'number' && roundEnd !== latchedRoundEnd && roundEnd > Date.now() + clockDrift) {
                acceptShift.current = false;
                setLatchedStart(s => s + (roundEnd - latchedRoundEnd));
                setLatchedRoundEnd(roundEnd);
            }
            return;
        }
        if (typeof roundEnd === 'number' && roundEnd > Date.now() + clockDrift) {
            setLatchedRoundEnd(roundEnd);
            setLatchedStart(Date.now() + clockDrift);
        }
    }, [roundEnd, clockDrift, latchedRoundEnd]);

    if (!latchedRoundEnd) return null;
    const now = countdownNow(clockDrift);
    const remainingMs = latchedRoundEnd - now;
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const totalDuration = latchedStart ? latchedRoundEnd - latchedStart : 0;
    const elapsed = latchedStart ? now - latchedStart : 0;
    const progress = totalDuration > 0 ? Math.min(1, Math.max(0, elapsed / totalDuration)) : 0;
    // Ease-in (cubic) so growth accelerates toward the end — most of the
    // visual change happens in the final stretch where it builds tension.
    const eased = progress * progress * progress;
    // Per-second pulse: each integer-second flip adds a brief bounce on
    // top of the steady growth so you can see the tick land.
    const fractionalSecond = (remainingMs / 1000) - Math.floor(remainingMs / 1000);
    const pulseDecay = 1 - fractionalSecond; // 0 right after a tick, 1 just before next
    const pulse = pulseDecay < 0.25 ? 0.18 * (1 - pulseDecay / 0.25) : 0;
    const scale = 1 + 1.1 * eased + pulse;
    const fadeWindow = 500;
    const opacity = remainingMs <= 0
        ? 0
        : remainingMs < fadeWindow
            ? remainingMs / fadeWindow
            : 1;
    const color =
        totalSeconds <= 10 ? '#ff3a3a' :
        totalSeconds <= 30 ? '#ffd24a' :
        '#ffffff';

    const isPolygon = typeof sides === 'number' && sides >= 3;

    // Default: chrome chronograph face (PNG with transparent corners) as
    // the badge backing, with the number centered on the dial.
    // With `sides`: a regular polygon backing instead.
    const backing = isPolygon ? (
        <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#000',
            clipPath: polygonClipPath(sides),
        }}/>
    ) : (
        <img
            src="/chronograph-gold.png"
            alt=""
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
                pointerEvents: 'none',
            }}
        />
    );

    return <div style={{
        position: 'fixed',
        top: 'calc(8px + env(safe-area-inset-top))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 72,
        height: 72,
        flexShrink: 0,
        zIndex: 1000,
        pointerEvents: 'none',
        filter: isPolygon ? 'drop-shadow(2px 2px 0 #ffffff)' : 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))',
        ...style,
    }}>
        {backing}
        <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            fontSize: 26,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            textShadow: '0 1px 2px rgba(0,0,0,0.95)',
            transform: `scale(${scale.toFixed(3)})`,
            transformOrigin: 'center',
            opacity,
            transition: 'opacity 120ms linear',
            willChange: 'transform, opacity',
        }}>{totalSeconds}</div>
    </div>;
};

// Live countdown for the operator status bar — does NOT latch like the
// player-facing CountdownBadge. We want operator to see the current
// roundEnd update in real time across scene changes.
export const OperatorCountdown = ({roundEnd, clockDrift = 0}) => {
    const [, force] = useState(0);
    useEffect(() => {
        const id = setInterval(() => force((n) => n + 1), 250);
        return () => clearInterval(id);
    }, []);
    if (typeof roundEnd !== 'number' || roundEnd <= 0) {
        return null;
    }
    const remainingMs = roundEnd - countdownNow(clockDrift);
    const secs = Math.max(0, Math.ceil(remainingMs / 1000));
    const color = remainingMs <= 0 ? '#ff6a6a' : secs <= 10 ? '#ff3a3a' : secs <= 30 ? '#ffd24a' : '#9be0ff';
    return <span style={{color, fontVariantNumeric: 'tabular-nums', fontWeight: 700}}>
        {remainingMs <= 0 ? 'overdue' : `${secs}s`}
    </span>;
};